import { format } from "date-fns";
import { dateSortComparator } from "../../../../utils/date";
import debounce from "lodash.debounce";
import fileDownload from "js-file-download";
import React, { useEffect, useState } from "react";

import { Box } from "@mui/system";
import { Button } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/Download";

import Title from "../../../common/Title";
import DataTable from "../../../common/DataTable";

import useAxios from "../../../../hooks/useAxios";
import {
  __getPwgMdnExportList,
  __getPwgMdnExportFile,
} from "../../../../api/sim";

const PWGExportList = () => {
  const [data, setData] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [metaData, setMetaData] = useState({
    page: 1,
    page_size: 100,
    total: 1,
  });
  const [response, call, loading] = useAxios({ ...__getPwgMdnExportList });
  const [fileResponse, fileCall, fileLoading] = useAxios({
    ...__getPwgMdnExportFile,
  });
  const [downloadFilename, setDownloadFilename] = useState(null);

  const debouncedFetchData = debounce((query) => {
    call(query);
  }, 500);

  useEffect(() => {
    debouncedFetchData({
      params: {
        page: currentPage,
        page_size: pageSize,
      },
    });

    return () => {
      debouncedFetchData.cancel();
    };
  }, [currentPage, pageSize]);

  useEffect(() => {
    if (response && response.status) {
      setMetaData({
        page: response.data.page,
        page_size: response.data.page_size,
        total: response.data.total,
      });
      setData(response.data.data);
    }
  }, [response]);

  useEffect(() => {
    if (fileResponse && fileResponse.status) {
      // The partner's file may be a .csv, .zip, or another format entirely -
      // use its real name (and extension) from the export row rather than
      // assuming one, so the downloaded file opens correctly.
      fileDownload(fileResponse.data, downloadFilename || "pwg-mdn-export");
    }
  }, [fileResponse]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize) => {
    setCurrentPage(1);
    setPageSize(newPageSize);
  };

  const handleDownload = (fileId, filename) => {
    setDownloadFilename(filename);
    fileCall({
      url: `sim/pwg_mdn_export/${fileId}`,
      responseType: "blob",
    });
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 1, sortable: false },
    {
      field: "filename",
      headerName: "Filename",
      minWidth: 200,
      flex: 3,
      sortable: false,
    },
    { field: "status", headerName: "Status", flex: 2, sortable: false },
    {
      field: "created_at",
      headerName: "Created At",
      minWidth: 200,
      flex: 3,
      sortable: false,
      valueGetter: (params) => params.row.created_at || null,
      valueFormatter: (params) =>
        params.value &&
        `${format(new Date(params.value), "MMMM d, y hh:mm a")}`,
      sortComparator: dateSortComparator,
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 150,
      flex: 2,
      sortable: false,
      renderCell: (params) =>
        params.row.status === "completed" ? (
          <Button
            variant="contained"
            size="small"
            disabled={fileLoading}
            onClick={() => handleDownload(params.row.id, params.row.filename)}
            startIcon={<FileDownloadIcon />}
          >
            Download
          </Button>
        ) : null,
    },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <Title variant="h5" content="PWG MDN Export History" gutterBottom />
      <DataTable
        disableColumnFilter
        loading={loading}
        data={data}
        columns={columns}
        metaData={metaData}
        handleRowClick={null}
        handlePageChange={handlePageChange}
        handlePageSizeChange={handlePageSizeChange}
      />
    </Box>
  );
};

export default PWGExportList;
